import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

/**
 * Paginator for grid/list. Emits 0-based page index (API convention).
 */
@Component({
  selector: 'app-paginator',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './paginator.component.html',
  styleUrls: ['./paginator.component.scss'],
})
export class PaginatorComponent implements OnInit, OnChanges {
  /** Total number of records */
  @Input() totalRecords = 0;
  /** Records per page */
  @Input() recordsPerPage = 10;
  /** Options for records per page dropdown */
  @Input() recordsPerPageOptions: number[] = [10, 25, 50, 100];
  /** Current page (0-based, from API) */
  @Input() page = 0;

  /** Emits 0-based page index when user changes page */
  @Output() pageChange = new EventEmitter<number>();
  /** Emits when user changes records per page (caller should reset to page 0 and reload) */
  @Output() recordsPerPageChange = new EventEmitter<number>();

  /** 1-based current page for display */
  currentPage = 1;
  /** Page numbers to show in the bar (1-based, or '...') */
  pages: (number | string)[] = [];

  ngOnInit(): void {
    this.currentPage = this.page + 1;
    this.updatePagination();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['page']) {
      this.currentPage = this.page + 1;
      this.updatePagination();
    }
    if (
      changes['totalRecords'] ||
      changes['recordsPerPage'] ||
      changes['recordsPerPageOptions']
    ) {
      const maxPage = this.totalPages;
      if (maxPage > 0 && this.currentPage > maxPage) {
        this.currentPage = maxPage;
      }
      this.updatePagination();
    }
  }

  get totalPages(): number {
    if (this.recordsPerPage <= 0) return 0;
    return Math.ceil(this.totalRecords / this.recordsPerPage) || 0;
  }

  onRecordsPerPageChange(): void {
    this.currentPage = 1;
    this.updatePagination();
    this.recordsPerPageChange.emit(this.recordsPerPage);
    this.pageChange.emit(0);
  }

  navigateTo(page: number | string): void {
    if (page === '...') return;
    const p = page as number;
    if (p < 1 || p > this.totalPages) return;
    this.currentPage = p;
    this.updatePagination();
    this.pageChange.emit(this.currentPage - 1);
  }

  getPaginationInfo(): string {
    if (this.totalRecords === 0) {
      return 'Nenhum registro';
    }
    const start = (this.currentPage - 1) * this.recordsPerPage + 1;
    const end = Math.min(this.currentPage * this.recordsPerPage, this.totalRecords);
    return `Mostrando ${start} a ${end} de ${this.totalRecords} registros`;
  }

  private updatePagination(): void {
    this.pages = this.getPageList();
  }

  private getPageList(): (number | string)[] {
    const total = this.totalPages;
    const current = this.currentPage;
    const wing = 2;

    if (total <= 5 + 2 * wing) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [1];
    if (current > wing + 2) {
      pages.push('...');
    }
    const start = Math.max(2, current - wing);
    const end = Math.min(total - 1, current + wing);
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    if (end < total - 1) {
      pages.push('...');
    }
    pages.push(total);
    return pages;
  }
}
